import {
  Alert,
  AlertDescription,
  AlertTitle,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { InlineField, NotPermitted, ParticipantAvatar } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { useCan } from '@/domains/auth';
import type { IOversightResponseRow } from '../../api/oversight-contracts';
import type {
  IAdminParticipantDetail,
  IAdminRoster,
  IAdminRosterMember
} from '../../api/participant-contracts';
import { participantsApi } from '../../api/participants-api';
import { absent } from '../../components/account-facts';
import { LoadError, Section, TableSkeleton, useAdminData } from '../../components/admin-page';
import { ParticipantLink } from '../../components/participant-link';
import { type ParticipantPick, ParticipantPicker } from '../../components/participant-picker';
import { TableFrame } from '../../components/table-frame';
import { refusalMessage } from '../../refusal';
import { slotWords } from '../../roster-words';
import { MemberPart, surveysOf } from './household';
import {
  type ISaidNote,
  NoteLine,
  NOT_REREAD,
  OUT_OF_DATE,
  unanswered,
  unansweredNote
} from './said';

/** A roster read as it stands — the roster last read (null before one landed) and the newest read's
 *  failure, if it failed. */
export interface IRosterRead {
  roster: Nullable<IAdminRoster>;
  error: Nullable<string>;
}

/**
 * A team's advisors and a firm's principals (SR-222, F7's R1–R3) — survey-api's
 * `GET …/participants/{id}/roster`: one row per slot its newest submitted `TEAMS` or `RIA_FIRM`
 * response names, each with the person survey-api resolves it to.
 *
 * **A section of its own, after Team** (the plan's Q1, ruled (a) on 2026-10-03). The Team section
 * is drawn for a team alone — a routing or an archive stamp — and RIA firms hold principals too
 * (2,707 rows on a real load), so a table inside Team would leave every firm's with no screen, and
 * would edit another block's section besides (K8's one entry per block). **Drawn while any member
 * is a `team` or an `org`**, decided from the hub document before any read, the Disclosures
 * section's rule: a person's roster is empty by construction, since a slot names a person and only
 * a team or a firm has slots.
 *
 * **Which of the two a member is, the kind cannot say**: survey-api registers a team and a firm
 * alike as an `org`. So a member's table is titled, and its emptiness said, by the survey it holds
 * on the hub (the owner's ruling, 2026-10-03) — a `TEAMS` response "Advisors", an `RIA_FIRM` one
 * "Principals", neither or both in words that claim neither. Known before the read, so the heading
 * never changes under it; words only, since slots exist on those two surveys' submits alone. A
 * slot's own words are its `role`'s.
 *
 * **Drawn as survey-api resolves it, and nothing here resolves anything** (R2). The rows in the
 * order sent, no sort, no count, no slot filled in; the identity is the team's answers, drawn as
 * answered and corrected on its response, never here; the member and how it was found are
 * survey-api's, first match winning at read — Veritant's decision, Salesforce's link, a person
 * holding the row's CRD. The role and resolution words are words, not mirrors: nothing branches on
 * them, and a value they lack is drawn as spelled.
 *
 * **Its own read per member, below a loaded hub, with the Activity's three arms** (R3) — a
 * skeleton, a failure said (a 404 is a failure, never an empty roster), and over a table already
 * drawn "showing the roster as last read". It re-reads on the page's `written` cue and **never on
 * the poll**: the poll is for a registry's answer, which arrives on the hub, while a roster moves
 * only on a submit, a correction or a decision. A CRD a person registers later resolves on the
 * page's next read, not on a tick.
 *
 * **Veritant's decision is one `InlineField` per slot, editing the decision, not the resolution**
 * (R4–R6). The field reads as the slot's member and opens on what overrides it: a person or Nobody
 * chosen in `ParticipantPicker`, or — while a decision stands — "No decision", which empties the
 * draft, so Save is the `DELETE` that hands the slot back to Salesforce's link or the CRD. Not a
 * confirmed press: the same field undoes it. A decision saved unchanged sends nothing. A write that
 * lands adopts the roster it answered and re-reads the hub (`reread`), whose `written` cue re-reads
 * every roster and memberships fact on the page; survey-api's 422 is said beside the field with the
 * draft kept; a 404 (the slot names nobody any more, or the team is gone) and an answer that never
 * arrived re-read, and **what they were told is held by the section, per team and slot**
 * (`useRosterNotes`) — the re-read can take the slot away, which is exactly when its sentence has
 * to stay, under "Slots no longer named". A decision is `responses.annotate` (SR-228): without it
 * every slot's pencil is drawn off, under one `NotPermitted` sentence per table.
 */
export function RosterSection({
  members,
  responses,
  search,
  written,
  reread,
  anchor,
  title = 'Advisors & principals',
  onRoster
}: {
  /** The whole household, the opened participant first. */
  members: IAdminParticipantDetail[];
  /** The household's responses — which survey each member holds (`rosterWords`). */
  responses: IOversightResponseRow[];
  search: string;
  written: number;
  /** Re-reads the hub after a write, and moves `written`. */
  reread: () => Promise<boolean>;
  anchor: string;
  /** "Advisors" on a team's record page, "Principals" on a firm's (SR-391) — the slots
   *  Salesforce named so on each object. */
  title?: string;
  /** Hands each part's read up as it lands — the record summary's lead (SR-400), never a second
   *  read of the roster. */
  onRoster?: (participantId: string, read: IRosterRead) => void;
}) {
  const multi = members.length > 1;
  const notes = useRosterNotes();
  return (
    <Section
      title={title}
      anchor={anchor}
      description={
        'Who each team’s and firm’s submitted survey names, and the person the system reads ' +
        'each slot as — Veritant’s decision first, then Salesforce’s link, then the CRD. Names, ' +
        'CRDs and emails are the survey’s answers, corrected on its response — not here.'
      }
    >
      <CardContent className="grid gap-8">
        {members.map((member, index) =>
          hasRoster(member) ? (
            <MemberPart
              key={member.participantId}
              member={member}
              opened={index === 0}
              multi={multi}
            >
              <MemberRoster
                participantId={member.participantId}
                displayName={member.displayName}
                words={rosterWords(responses, member.participantId)}
                search={search}
                written={written}
                reread={reread}
                notes={notes}
                multi={multi}
                onRoster={onRoster}
              />
            </MemberPart>
          ) : null
        )}
      </CardContent>
    </Section>
  );
}

/** Whether a member can hold a roster — a team or a firm (R1). */
export function hasRoster(member: IAdminParticipantDetail): boolean {
  return member.kind === 'team' || member.kind === 'org';
}

interface IRosterWords {
  title: string;
  empty: string;
}

const TEAM_WORDS: IRosterWords = {
  title: 'Advisors',
  empty: 'No submitted Teams survey names advisors yet.'
};
const FIRM_WORDS: IRosterWords = {
  title: 'Principals',
  empty: 'No submitted RIA Firm survey names principals yet.'
};
const EITHER_WORDS: IRosterWords = {
  title: 'Named slots',
  empty: 'No submitted Teams or RIA Firm survey names anybody yet.'
};

/** A member's words by the survey it holds on the hub — a team's, a firm's, or neither's. */
function rosterWords(responses: IOversightResponseRow[], participantId: string): IRosterWords {
  const surveys = surveysOf(responses, participantId);
  const team = surveys.includes('TEAMS');
  const firm = surveys.includes('RIA_FIRM');
  if (team === firm) return EITHER_WORDS;
  return team ? TEAM_WORDS : FIRM_WORDS;
}

function MemberRoster({
  participantId,
  displayName,
  words,
  search,
  written,
  reread,
  notes,
  multi,
  onRoster
}: {
  participantId: string;
  displayName: string;
  words: IRosterWords;
  search: string;
  written: number;
  reread: () => Promise<boolean>;
  notes: IRosterNotes;
  multi: boolean;
  onRoster?: (participantId: string, read: IRosterRead) => void;
}) {
  const { data, error, adopt } = useAdminData(
    (signal) => participantsApi.getRoster(participantId, signal),
    [participantId, written]
  );
  React.useEffect(() => {
    onRoster?.(participantId, { roster: data, error });
  }, [onRoster, participantId, data, error]);
  const Heading = multi ? 'h4' : 'h3';
  const headingId = React.useId();
  const said = notes.said[participantId] ?? {};
  // A slot's note is drawn in its row while survey-api still names the slot, and under the table
  // once a re-read has taken it away — never dropped (R6).
  const named = new Set(data?.members.map((row) => row.slot) ?? []);
  const vanished =
    data === null
      ? []
      : Object.entries(said)
          .flatMap(([key, note]) => {
            const slot = Number(key);
            return note !== undefined && !named.has(slot) ? [{ slot, note }] : [];
          })
          .sort((a, b) => a.slot - b.slot);

  const decision: IDecisionContext = {
    teamId: participantId,
    teamName: displayName,
    adopt,
    reread,
    say: (slot, note) => notes.say(participantId, slot, note)
  };

  return (
    <div data-roster={participantId} className="grid gap-3">
      <Heading id={headingId} className="text-sm font-semibold">
        {words.title}
      </Heading>
      {error &&
        (data === null ? (
          <LoadError message={error} />
        ) : (
          <Alert variant="destructive">
            <AlertTitle>Could not refresh — showing the roster as last read</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ))}
      {data === null && !error && <TableSkeleton rows={2} />}
      {data !== null &&
        (data.members.length === 0 ? (
          <p className="text-sm">{absent(words.empty)}</p>
        ) : (
          <>
            <RosterTable
              rows={data.members}
              labelledBy={headingId}
              search={search}
              said={said}
              decision={decision}
            />
          </>
        ))}
      {vanished.length > 0 && (
        <div data-vanished-slots className="grid gap-2">
          <p className="text-sm font-medium">Slots no longer named</p>
          {vanished.map(({ slot, note }) => (
            <div key={slot} data-slot-note={slot}>
              <NoteLine
                note={{ ...note, title: note.title ? `Slot ${slot} — ${note.title}` : `Slot ${slot}` }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RosterTable({
  rows,
  labelledBy,
  search,
  said,
  decision
}: {
  rows: IAdminRosterMember[];
  labelledBy: string;
  search: string;
  said: Partial<Record<number, ISaidNote>>;
  decision: IDecisionContext;
}) {
  const permitted = useCan()('responses.annotate');
  const reasonId = React.useId();
  return (
    <>
      {!permitted && <NotPermitted id={reasonId} what="deciding who fills a named slot" />}
      <TableFrame>
        <Table aria-labelledby={labelledBy}>
          <TableHeader>
            <TableRow>
              <TableHead>Slot</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>CRD</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Member</TableHead>
              <TableHead>Resolved by</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const note = said[row.slot];
              return (
                <TableRow
                  key={row.slot}
                  data-slot={row.slot}
                  data-resolved-by={row.resolvedBy ?? 'none'}
                >
                  <TableCell className="whitespace-nowrap">{slotWords(row)}</TableCell>
                  <TableCell>{row.fullName ?? absent('Not answered')}</TableCell>
                  <TableCell className="tabular-nums">{row.crd ?? absent('Not answered')}</TableCell>
                  <TableCell className="break-all">{row.email ?? absent('Not answered')}</TableCell>
                  <TableCell className="min-w-56">
                    <SlotDecision
                      row={row}
                      search={search}
                      context={decision}
                      notPermitted={permitted ? undefined : reasonId}
                    />
                    {note && (
                      <div className="mt-2">
                        <NoteLine note={note} />
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{resolvedByWords(row.resolvedBy)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableFrame>
    </>
  );
}

/** What a slot's field needs of its team's part. */
interface IDecisionContext {
  teamId: string;
  teamName: string;
  /** Draws the roster a write answered, before the hub's re-read. */
  adopt: (roster: IAdminRoster) => void;
  reread: () => Promise<boolean>;
  say: (slot: number, note: Nullable<ISaidNote>) => void;
}

/** The decision standing on a slot — a person, or Nobody — or `null` where none does, whatever
 *  Salesforce's link or the CRD resolve it to. */
function decisionOf(row: IAdminRosterMember): Nullable<ParticipantPick> {
  if (row.resolvedBy !== 'staff') return null;
  return row.member === null ? { kind: 'nobody' } : { kind: 'person', ...row.member };
}

function samePick(a: Nullable<ParticipantPick>, b: Nullable<ParticipantPick>): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind === 'person' && b.kind === 'person') return a.participantId === b.participantId;
  return a.kind === b.kind;
}

/** Veritant's decision on one slot (R4): the field reads as the slot's member, and edits what
 *  overrides it. */
function SlotDecision({
  row,
  search,
  context,
  notPermitted
}: {
  row: IAdminRosterMember;
  search: string;
  context: IDecisionContext;
  /** The id of the table's `NotPermitted` sentence; present, the pencil is drawn off. */
  notPermitted?: string;
}) {
  const decided = decisionOf(row);
  const where = `${slotWords(row)} of ${context.teamName}`;

  return (
    <InlineField<Nullable<ParticipantPick>>
      label="Member"
      name={where}
      fieldKey={`roster.${context.teamId}.${row.slot}`}
      notPermitted={notPermitted}
      display={<SlotMember row={row} search={search} />}
      value={decided}
      isEmpty={(draft) => draft === null}
      renderControl={(control) => (
        <ParticipantPicker
          label={control.label}
          value={control.value}
          onChange={control.onChange}
          disabled={control.disabled}
          initialQuery={row.crd ?? row.fullName ?? ''}
          nobody="Nobody"
          none={
            decided === null ? undefined : 'No decision — let Salesforce’s link or the CRD decide'
          }
        />
      )}
      onSave={async (draft) => {
        if (draft === null || samePick(draft, decided)) return null;
        return rosterWrite(
          () =>
            participantsApi.setRosterMember(
              context.teamId,
              row.slot,
              draft.kind === 'person' ? draft.participantId : null
            ),
          draft,
          where,
          row.slot,
          context
        );
      }}
      onClear={() =>
        rosterWrite(
          () => participantsApi.clearRosterMember(context.teamId, row.slot),
          null,
          where,
          row.slot,
          context
        )
      }
    />
  );
}

/**
 * One decision's outcome, in the hub's words (R6). A landing adopts the roster it answered — so
 * the field closes over the new member — then re-reads the hub. survey-api's 422 is the field's
 * refusal, the draft kept and nothing re-read; so is any other refusal, in survey-api's words. A
 * 404 and an answer that never arrived re-read and close the field, and are said by the section
 * (`say`), which outlives the slot.
 */
async function rosterWrite(
  send: () => Promise<IAdminRoster>,
  chosen: Nullable<ParticipantPick>,
  where: string,
  slot: number,
  { adopt, reread, say }: IDecisionContext
): Promise<Nullable<string>> {
  say(slot, null);
  try {
    adopt(await send());
    if (!(await reread())) say(slot, { tone: 'done', text: `Saved.${NOT_REREAD}` });
    return null;
  } catch (error) {
    if (unanswered(error)) {
      say(slot, unansweredNote(await reread(), 'slot'));
      return null;
    }
    if (error instanceof ApiError && error.status === 404) {
      const read = await reread();
      say(slot, {
        tone: 'refused',
        title: 'Not saved',
        text:
          `The system names nobody in ${where} any more — the page was out of date. ` +
          (read ? 'It has been read again.' : OUT_OF_DATE)
      });
      return null;
    }
    if (error instanceof ApiError && error.isProblem(ProblemType.rosterMemberNotPerson)) {
      const name = chosen?.kind === 'person' ? chosen.displayName : 'that participant';
      return `Only a person can sit in a slot — the system does not hold ${name} as one.`;
    }
    return refusalMessage(error, 'The decision was not saved.');
  }
}

interface IRosterNotes {
  /** Per team, per slot: what the slot's last write was told. */
  said: Record<string, Partial<Record<number, ISaidNote>>>;
  say: (teamId: string, slot: number, note: Nullable<ISaidNote>) => void;
}

/** What each slot's decision was told, held by the section rather than the field — see the
 *  section's JSDoc. */
function useRosterNotes(): IRosterNotes {
  const [said, setSaid] = React.useState<IRosterNotes['said']>({});
  const say = React.useCallback<IRosterNotes['say']>((teamId, slot, note) => {
    setSaid((current) => ({
      ...current,
      [teamId]: { ...current[teamId], [slot]: note ?? undefined }
    }));
  }, []);
  return { said, say };
}

/** Who sits in the slot, as survey-api read it — a person, Veritant's decision of nobody, or no one
 *  found. */
function SlotMember({ row, search }: { row: IAdminRosterMember; search: string }) {
  if (row.member !== null) {
    return (
      <span className="flex items-center gap-2">
        <ParticipantAvatar
          photoUri={row.member.photoUri}
          displayName={row.member.displayName}
          size="sm"
          decorative
        />
        <ParticipantLink participant={row.member} search={search} />
      </span>
    );
  }
  return row.resolvedBy === 'staff' ? (
    <span>Nobody — Veritant’s decision</span>
  ) : (
    absent('Not resolved')
  );
}

/** How survey-api found the slot's member; nothing for none, an unknown way as spelled. */
const RESOLVED_BY_WORDS: Readonly<Record<string, string>> = {
  staff: 'Veritant’s decision',
  salesforce: 'Salesforce’s link',
  crd: 'By CRD'
};

function resolvedByWords(resolvedBy: Nullable<string>): string {
  return resolvedBy === null ? '' : (RESOLVED_BY_WORDS[resolvedBy] ?? resolvedBy);
}

/** The same, as the end of a sentence — "Advisor 3, by CRD" on a person's Overview card. */
const RESOLVED_BY_PHRASES: Readonly<Record<string, string>> = {
  staff: 'by Veritant’s decision',
  salesforce: 'by Salesforce’s link',
  crd: 'by CRD',
  // The fourth step since SR-414: a person whose email the slot's answers name.
  email: 'by email'
};

export function resolvedByPhrase(resolvedBy: string): string {
  return RESOLVED_BY_PHRASES[resolvedBy] ?? `by ${resolvedBy}`;
}
